import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ForkliftIcon from "@mui/icons-material/Agriculture";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Dialog, Tooltip } from "@mui/material";
import { capitalizeFirstLetter, normalizeDate } from "../../../utilities";
import { LoadingState } from "../../../components/UI/States";
import { fetchKioskBoard, setKioskService } from "../fleetApi";
import { SERVICE_REASONS } from "../../../components/Fleet/fleetFormat";

// TV / kiosk display (no app header). Kept deliberately simple: large type,
// solid status colors, no blur layers.
//
// Branches that run shifts (SBC) get a shift switcher; the chosen shift is
// remembered on this device and every check-in records it. Old per-shift
// kiosk addresses (/dashboard/sbc2) land here on the merged branch with
// that shift picked.

const REFRESH_MS = 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

const shiftStorageKey = (branch) => `fleet.kiosk.shift.${branch.toLowerCase()}`;
const readStoredShift = (branch) => {
  try {
    return localStorage.getItem(shiftStorageKey(branch));
  } catch (e) {
    return null;
  }
};
const storeShift = (branch, shift) => {
  try {
    localStorage.setItem(shiftStorageKey(branch), shift);
  } catch (e) {
    // Private mode / blocked storage: the URL still carries the shift.
  }
};

// Freshness of a unit's last check-in: green within a shift (12h), amber
// within a day and a half, red beyond.
const freshness = (at) => {
  const age = at ? Date.now() - new Date(at).getTime() : Infinity;
  if (age <= 12 * HOUR_MS) return "border-emerald-500/40 bg-emerald-500/15 text-emerald-800 dark:text-emerald-200";
  if (age <= 36 * HOUR_MS) return "border-amber-500/40 bg-amber-500/15 text-amber-900 dark:text-amber-200";
  return "border-rose-500/40 bg-rose-500/15 text-rose-800 dark:text-rose-200";
};

const shortTime = (at) =>
  new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const ContactBlock = ({ title, name, email, phone }) => (
  <div className="app-surface min-w-0 rounded-xl border p-3">
    <p className="app-eyebrow">{title}</p>
    <p className="mt-1 truncate text-lg font-semibold">{name || "—"}</p>
    {email && (
      <p className="app-muted mt-0.5 flex items-center gap-1.5 truncate text-sm">
        <EmailIcon sx={{ fontSize: 16 }} />
        <span className="truncate">{email}</span>
      </p>
    )}
    {phone && (
      <p className="app-muted mt-0.5 flex items-center gap-1.5 text-sm tabular-nums">
        <PhoneIcon sx={{ fontSize: 16 }} />
        {phone}
      </p>
    )}
  </div>
);

// One chip per shift: green with the time when that shift checked the unit
// in the last 12 hours, otherwise a dash.
const ShiftChecks = ({ shifts, byShift, current }) => (
  <div className="mt-3 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${shifts.length}, minmax(0, 1fr))` }}>
    {shifts.map((shift) => {
      const check = byShift[shift.id];
      const done = check && Date.now() - new Date(check.at).getTime() <= 12 * HOUR_MS;
      return (
        <div
          key={shift.id}
          title={check ? `${shift.name}: ${check.inspector || "checked"} at ${normalizeDate(check.at)}` : `${shift.name}: no check-in yet`}
          className={`flex min-w-0 flex-col items-center rounded-lg border px-1.5 py-1 text-center text-xs ${
            done
              ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-800 dark:text-emerald-200"
              : "app-surface app-muted"
          } ${shift.id === current ? "ring-2 ring-sky-500" : ""}`}
        >
          <span className="truncate font-semibold">{shift.name}</span>
          <span className="flex items-center gap-0.5 tabular-nums">
            {done ? (
              <>
                <CheckCircleRoundedIcon sx={{ fontSize: 13 }} /> {shortTime(check.at)}
              </>
            ) : (
              "—"
            )}
          </span>
        </div>
      );
    })}
  </div>
);

const ForkDisplay = () => {
  const { branch } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  // Taking a unit out of service needs a reason (and details for "Other").
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [by, setBy] = useState("");

  const openConfirm = (item) => {
    setReason("");
    setNote("");
    setBy("");
    setConfirm(item);
  };
  const goingOut = Boolean(confirm?.inService);
  const canSave = !goingOut || (reason && (reason !== "other" || note.trim()));

  const load = useCallback(async () => {
    try {
      setBoard(await fetchKioskBoard(branch));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [branch]);

  useEffect(() => {
    setBoard(null);
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Old per-shift kiosk address: move to the merged branch on that shift.
  useEffect(() => {
    if (board?.aliasShift && board.branch.toLowerCase() !== branch.toLowerCase()) {
      navigate(
        `/fleet/forks/dashboard/${encodeURIComponent(board.branch)}?shift=${encodeURIComponent(board.aliasShift)}`,
        { replace: true }
      );
    }
  }, [board, branch, navigate]);

  const shifts = useMemo(() => board?.shifts || [], [board]);
  const requested = params.get("shift");
  const currentShift = useMemo(() => {
    if (!shifts.length || !board) return "";
    const valid = (id) => id && shifts.some((s) => s.id === id);
    if (valid(requested)) return requested;
    const stored = readStoredShift(board.branch);
    return valid(stored) ? stored : shifts[0].id;
  }, [shifts, requested, board]);

  // A shift in the address (an old sbc2 bookmark, a switcher tap) becomes
  // this device's default.
  useEffect(() => {
    if (board && currentShift) storeShift(board.branch, currentShift);
  }, [board, currentShift]);

  const shift = shifts.find((s) => s.id === currentShift) || null;
  const branchName = board?.branch || capitalizeFirstLetter(branch);

  const toggleService = async () => {
    if (!confirm || busy || !canSave) return;
    setBusy(true);
    try {
      await setKioskService({
        id: confirm.id,
        inService: !confirm.inService,
        reason: goingOut ? reason : undefined,
        note: note.trim(),
        by: by.trim(),
      });
      setConfirm(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const checklistLink = (item) => {
    const query = new URLSearchParams({ m: String(item.moffett) });
    if (shift) {
      query.set("shift", shift.id);
      query.set("sn", shift.name);
    }
    return `/fleet/forks/forkliftform/${encodeURIComponent(branchName)}/${encodeURIComponent(item.unit)}?${query}`;
  };

  return (
    <div className="app-page min-h-screen">
      <Dialog fullWidth maxWidth="sm" open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        {confirm && (
          <div className="flex flex-col items-center gap-5 p-6 text-center sm:p-8">
            <p className="text-lg">
              {goingOut ? "Why is" : "Put"} unit <strong>#{confirm.unit}</strong>{" "}
              {goingOut ? "going out of service?" : "back in service?"}
            </p>
            {goingOut && (
              <div role="radiogroup" aria-label="Reason" className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                {SERVICE_REASONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={reason === option.value}
                    onClick={() => setReason(option.value)}
                    className={`app-btn app-btn-lg w-full ${
                      reason === option.value ? "app-btn-primary" : "app-btn-secondary"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            <div className="grid w-full gap-2 text-left">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                placeholder={
                  goingOut
                    ? reason === "other"
                      ? "What's wrong? (required)"
                      : "Details (optional)"
                    : "What was done? (optional)"
                }
                aria-label="Details"
                className="app-input w-full rounded-xl border px-4 py-3 text-base"
              />
              <input
                type="text"
                value={by}
                onChange={(e) => setBy(e.target.value)}
                maxLength={100}
                placeholder="Your name"
                aria-label="Your name"
                autoComplete="name"
                className="app-input w-full rounded-xl border px-4 py-3 text-base"
              />
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={toggleService}
                disabled={busy || !canSave}
                className={`app-btn app-btn-lg min-w-[7rem] ${
                  confirm.inService ? "app-btn-danger" : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
              >
                {busy ? "Saving…" : goingOut ? "Mark out of service" : "Mark in service"}
              </button>
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="app-btn app-btn-lg app-btn-secondary min-w-[7rem]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Dialog>

      <div className="mx-auto w-full max-w-[110rem] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <span className="app-icon-tile h-14 w-14 rounded-2xl bg-teal-500/15 text-teal-600 dark:text-teal-300">
              <ForkliftIcon fontSize="large" />
            </span>
            <div className="min-w-0">
              <p className="app-eyebrow text-sm">Forklift Check-In</p>
              <h1 className="truncate text-3xl font-bold tracking-tight sm:text-4xl">{branchName} Forklifts</h1>
            </div>
          </div>

          {shifts.length > 0 && (
            <div role="group" aria-label="Shift" className="flex flex-wrap gap-2">
              {shifts.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={s.id === currentShift}
                  onClick={() => setParams({ shift: s.id }, { replace: true })}
                  className={`app-btn app-btn-lg min-w-[7.5rem] ${
                    s.id === currentShift ? "app-btn-primary" : "app-btn-secondary"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </header>

        {error && (
          <p role="alert" className="mb-4 rounded-xl bg-rose-500/10 px-4 py-3 text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}

        {!board && !error && <LoadingState label="Loading forklifts…" />}

        {board && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            <section className="app-card flex flex-col rounded-2xl border p-5">
              <h2 className="text-xl font-bold">Manager Information</h2>
              <div className="mt-4 flex flex-col gap-3">
                {shift?.managerName && (
                  <ContactBlock
                    title={`${shift.name} supervisor`}
                    name={shift.managerName}
                    email={shift.managerEmail}
                    phone={shift.managerPhone}
                  />
                )}
                <ContactBlock
                  title="Operations"
                  name={board.contacts?.managerName}
                  email={board.contacts?.managerEmail}
                  phone={board.contacts?.managerPhone}
                />
                <ContactBlock
                  title="General Manager"
                  name={board.contacts?.generalName}
                  email={board.contacts?.generalEmail}
                  phone={board.contacts?.generalPhone}
                />
              </div>
            </section>

            {board.forklifts.map((item) => (
              <section
                key={item.id}
                className={`app-card flex flex-col rounded-2xl border p-5 ${item.inService ? "" : "opacity-80"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-2xl font-bold tabular-nums sm:text-3xl">Unit #{item.unit}</p>
                  {item.moffett && <span className="app-chip shrink-0 text-sm">Moffett</span>}
                </div>

                <Link
                  to={`/fleet/forks/entries/${encodeURIComponent(item.unit)}?branch=${encodeURIComponent(item.location)}`}
                  className={`mt-4 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition hover:opacity-90 ${freshness(item.lastChecked)}`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-bold uppercase tracking-wide">Last Check-In</span>
                    <span className="block text-lg font-semibold tabular-nums">
                      {item.lastChecked ? normalizeDate(item.lastChecked) : "Never"}
                    </span>
                  </span>
                  <ArrowOutwardIcon className="shrink-0 text-current" />
                </Link>

                {shifts.length > 0 && <ShiftChecks shifts={shifts} byShift={item.byShift} current={currentShift} />}

                {!item.inService && item.service && (
                  <p className="mt-3 rounded-xl bg-violet-500/15 px-3 py-2 text-sm text-violet-800 dark:text-violet-200">
                    <strong>Out of service:</strong> {item.service.reasonLabel}
                    {item.service.note ? ` — ${item.service.note}` : ""}
                  </p>
                )}

                <div className="mt-4 grid grid-cols-2 gap-3">
                  {item.inService ? (
                    <Link to={checklistLink(item)} className="app-btn app-btn-lg app-btn-primary w-full">
                      Check In
                    </Link>
                  ) : (
                    <Tooltip title="Not Currently In Service">
                      <button type="button" aria-disabled="true" className="app-btn app-btn-lg app-btn-secondary w-full">
                        Check In
                      </button>
                    </Tooltip>
                  )}
                  <button
                    type="button"
                    onClick={() => openConfirm(item)}
                    className={`app-btn app-btn-lg w-full ${
                      item.inService ? "bg-emerald-600 text-white hover:bg-emerald-700" : "app-btn-danger"
                    }`}
                  >
                    {item.inService ? "In Service" : "Out of Service"}
                  </button>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ForkDisplay;
